import logger from '../../lib/logger';
import React, { useState, useRef } from 'react';
import { tokenStore } from '../../lib/tokenStore';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
} from '../ui/menubar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { AlertTriangle, Ruler, Link, Copy, Check, Settings, Shield, LogOut, User, FileText, BookOpen, Download, Upload, Zap, MessageCircle, Pencil } from 'lucide-react';
import { exportProjectionReport } from './ProjectionReportExport';
import { exportProject, inspectPackage, commitImport } from '../../lib/projectPackage';
import ImportProjectModal from '../Modals/ImportProjectModal';
import PerformanceGuideModal from '../Help/PerformanceGuideModal';
import ContactSupportModal from '../Help/ContactSupportModal';
import TermsModal from '../Legal/TermsModal';
import { supportApi, projectsApi } from '../../lib/api';

const MenuBar = ({ onNewProject, onOpenProjects, onExport, onOpenManual }) => {
  const { 
    currentProject, 
    sceneObjects, 
    projectors, 
    addSceneObject,
    addProjector,
    isDirty,
    worldSettings,
    setWorldSettings,
    projectSettings,
    setProjectSettings,
    saveProject,
    saveProjectAs,
    saveToCloud,
    undo,
    redo,
    canUndo,
    canRedo,
    duplicateSelected,
    duplicateMultiSelected,
    deleteSelected,
    selectAll,
    deselectAll,
    selectedObjectId,
    setViewPreset,
    toggleGrid,
    measurementMode,
    setMeasurementMode,
    clearAllMeasurements,
    showAllBeams,
    hideAllBeams,
    editMode,
    setControlMode,
    projectorLibrary,
    setLeftPanelOpen,
    setActiveLeftTab,
    openAddProjectorDialog,
    setIsDirty,
    clearProject,
    multiSelectIds,
    setMultiSelectIds,
    removeProjector,
    removeSceneObject,
    savedCameras,
    overlapAnalysis,
    calculateOverlaps,
    getCameraStateRef,
    captureScreenshotRef,
    createCamera,
    updateProjector,
    measurements,
    loadProject,
    setCurrentProject
  } = useApp();

  const { user, isAdmin, logout } = useAuth();

  // Shareable link dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // ── Export / Import (.avproj) ──
  const importInputRef = useRef(null);
  const [importPkg, setImportPkg] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);

  // ── Performance & Setup Guide modal ──
  const [perfGuideOpen, setPerfGuideOpen] = useState(false);

  // ── Contact Support modal + unread badge ──
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);

  // ── Terms & Conditions modal ──
  const [termsOpen, setTermsOpen] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await supportApi.unreadCount();
        if (!cancelled) setSupportUnread(res.data?.count || 0);
      } catch (e) { /* silent — non-critical */ }
    };
    refresh();
    // Poll every 60s — async, lightweight, never blocks editor
    const id = setInterval(refresh, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [supportOpen]);

  const handleExportProject = async () => {
    try {
      // Capture preview thumbnail from the editor canvas (best-effort)
      let previewDataUrl = null;
      try {
        const canvas = document.querySelector('canvas');
        if (canvas && canvas.toDataURL) {
          previewDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        }
      } catch (e) { /* preview optional */ }

      toast.loading('Exporting project…', { id: 'avproj-export' });
      const { blob, fileName, stats, manifest } = await exportProject({
        sceneObjects, projectors, worldSettings, projectSettings,
        measurements: measurements || [], savedCameras: savedCameras || [],
        projectName: projectSettings?.name || currentProject?.name,
      }, {
        previewDataUrl,
        onProgress: (pct, label) => {
          toast.loading(`${label} (${pct}%)`, { id: 'avproj-export' });
        },
      });

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
      const missingNote = manifest.stats.missingAssets > 0
        ? ` · ${manifest.stats.missingAssets} missing asset${manifest.stats.missingAssets === 1 ? '' : 's'}`
        : '';
      toast.success(`Exported ${fileName} (${sizeMB} MB · ${stats.assets} assets${missingNote})`,
        { id: 'avproj-export', duration: 4500 });
    } catch (err) {
      logger.error('Export project error:', err);
      toast.error(`Export failed: ${err.message || 'Unknown error'}`, { id: 'avproj-export' });
    }
  };

  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Allow re-selecting the same file later
    e.target.value = '';

    try {
      toast.loading('Reading package…', { id: 'avproj-import' });
      const pkg = await inspectPackage(file);
      toast.dismiss('avproj-import');
      setImportPkg(pkg);
      setImportModalOpen(true);
    } catch (err) {
      logger.error('Inspect package error:', err);
      toast.error(`Could not read package: ${err.message || 'Invalid .avproj file'}`,
        { id: 'avproj-import' });
    }
  };

  const handleImportConfirm = async ({ projectName, onProgress }) => {
    if (!importPkg) return;
    const { project } = await commitImport(importPkg, {
      projectNameOverride: projectName,
      onProgress,
    });
    // Cleanup blob URL for preview
    if (importPkg.previewUrl) {
      try { URL.revokeObjectURL(importPkg.previewUrl); } catch (e) {}
    }
    // Close modal and load the new project into the editor
    setImportModalOpen(false);
    setImportPkg(null);
    if (loadProject) loadProject(project);
    toast.success(`Imported "${project.name}"`, { duration: 3500 });
  };

  const handleGetShareableLink = async () => {
    try {
      const token = tokenStore.get();
      const API = process.env.REACT_APP_BACKEND_URL;
      
      let projId = currentProject?.id;
      
      // If no project exists yet, create one first
      if (!projId) {
        const createRes = await fetch(`${API}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            name: projectSettings?.name || 'Untitled Project',
            description: '',
            scene_objects: sceneObjects || [],
            projectors: projectors || [],
            world_settings: worldSettings || {},
            project_settings: projectSettings || {},
            unit: projectSettings?.unit || 'meters'
          })
        });
        const created = await createRes.json();
        projId = created.id;
        if (!projId) {
          toast.error('Failed to create project. Please try File > Save first.');
          return;
        }
        toast.success('Project saved');
      } else {
        // Save latest state
        await saveProject();
      }

      // Generate share link
      const res = await fetch(`${API}/api/projects/${projId}/share`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.share_token) {
        const baseUrl = window.location.origin;
        const viewerUrl = `${baseUrl}/sim-share/${data.share_token}`;
        setShareLink(viewerUrl);
        setShareDialogOpen(true);
        setLinkCopied(false);
      } else {
        toast.error('Failed to generate share link');
      }
    } catch (err) {
      logger.error('Share error:', err);
      toast.error('Failed to generate share link');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleQuickSave = () => {
    try {
      const saveData = {
        sceneObjects: sceneObjects.map(obj => ({ ...obj, modelData: undefined })),
        projectors,
        worldSettings,
        projectSettings,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('iav_project_autosave', JSON.stringify(saveData));
      setIsDirty(false);
      toast.success('Project saved locally');
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleQuickLoad = () => {
    try {
      const saved = localStorage.getItem('iav_project_autosave');
      if (saved) {
        const data = JSON.parse(saved);
        // Load without page refresh
        if (data.sceneObjects) {
          // Use context methods to load data
          clearProject();
          // Wait a tick for state to clear
          setTimeout(() => {
            if (data.worldSettings) setWorldSettings(data.worldSettings);
            if (data.projectSettings) setProjectSettings(data.projectSettings);
            // Load objects one by one to trigger proper state updates
            data.sceneObjects?.forEach(obj => {
              addSceneObject({ ...obj, id: obj.id });
            });
            data.projectors?.forEach(proj => {
              addProjector(proj.spec || proj);
            });
            toast.success(`Loaded project from ${new Date(data.timestamp).toLocaleString()}`);
          }, 50);
        }
      } else {
        toast.info('No saved project found');
      }
    } catch (error) {
      toast.error('Failed to load project');
      logger.error(error);
    }
  };

  const handleSave = async () => {
    try {
      await saveProject();
      toast.success('Project saved');
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save project');
    }
  };

  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [saveAsNameVal, setSaveAsNameVal] = useState('');

  // Inline project rename — click the project name in the menubar to edit
  // in-place, Enter/blur to commit, Esc to cancel.
  const [renamingProject, setRenamingProject] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef(null);

  const startRenameProject = () => {
    setRenameDraft(projectSettings?.name || currentProject?.name || 'Untitled');
    setRenamingProject(true);
    // Focus + select after the input mounts
    setTimeout(() => {
      try {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      } catch (_e) {}
    }, 0);
  };
  const commitRenameProject = async () => {
    const next = (renameDraft || '').trim();
    setRenamingProject(false);
    if (!next) return;
    const prev = projectSettings?.name || currentProject?.name || '';
    if (next === prev) return;
    // Update both stores so the visible label updates immediately whether
    // the menu reads from `currentProject.name` (preferred when loaded) or
    // `projectSettings.name` (fallback for unsaved projects).
    setProjectSettings({ ...projectSettings, name: next });
    if (currentProject?.id) {
      setCurrentProject({ ...currentProject, name: next });
      // Persist to the backend so the new name survives refresh / reopen
      // even if the user doesn't trigger an explicit save afterwards.
      try {
        await projectsApi.update(currentProject.id, { name: next });
      } catch (e) {
        logger.warn('Project rename persist failed:', e);
      }
    }
    setIsDirty(true);
    toast.success('Project renamed');
  };
  const cancelRenameProject = () => {
    setRenamingProject(false);
    setRenameDraft('');
  };

  const handleSaveAsClick = () => {
    setSaveAsNameVal((projectSettings?.name || currentProject?.name || 'Untitled') + ' - Copy');
    setSaveAsDialogOpen(true);
  };

  const handleSaveAsConfirm = async () => {
    if (!saveAsNameVal.trim()) { toast.error('Enter a project name'); return; }
    try {
      await saveProjectAs(saveAsNameVal.trim());
      toast.success(`Saved as "${saveAsNameVal.trim()}"`);
      setSaveAsDialogOpen(false);
    } catch (error) {
      logger.error('Save As error:', error);
      toast.error('Failed to save');
    }
  };

  const [projReportExporting, setProjReportExporting] = useState(false);

  const handleExportProjectionReport = async () => {
    if (projectors.length === 0) {
      toast.error('No projectors in the scene');
      return;
    }
    setProjReportExporting(true);
    toast.info('Generating projection report...');
    try {
      const scene = window.__babylonScene;
      const engine = window.__babylonEngine;
      const fileName = await exportProjectionReport({
        projectors,
        sceneObjects,
        savedCameras,
        overlapAnalysis,
        calculateOverlaps,
        projectName: projectSettings?.name || currentProject?.name || 'Untitled',
        userName: user?.name || user?.email || 'System User',
        projectSettings,
        scene,
        engine
      });
      toast.success(`Report exported: ${fileName}`);
    } catch (error) {
      logger.error('Projection report error:', error);
      toast.error('Failed to export report: ' + error.message);
    } finally {
      setProjReportExporting(false);
    }
  };

  const handleRenameProject = () => {
    const newName = prompt('Enter new project name:', projectSettings.name || 'Untitled');
    if (newName && newName.trim()) {
      setProjectSettings({ ...projectSettings, name: newName.trim() });
      toast.success('Project renamed');
    }
  };

  // Track last add time to prevent double-adds
  const lastAddTimeRef = React.useRef(0);
  
  const addPrimitive = (type, name, options = {}) => {
    // Prevent double-adds within 500ms
    const now = Date.now();
    if (now - lastAddTimeRef.current < 500) {
      return;
    }
    lastAddTimeRef.current = now;
    
    // Default dimensions in meters - realistic sizes for projection mapping
    const defaults = {
      box: { dimensions: { width: 1, height: 1, depth: 1 }, position: { x: 0, y: 0.5, z: 0 } },
      sphere: { dimensions: { width: 1, height: 1, depth: 1 }, position: { x: 0, y: 0.5, z: 0 } },
      plane: { dimensions: { width: 3, height: 2, depth: 0.01 }, position: { x: 0, y: 1, z: 3 } },
      cylinder: { dimensions: { width: 1, height: 2, depth: 1 }, position: { x: 0, y: 1, z: 0 } },
      ground: { dimensions: { width: 10, height: 10, depth: 0.01 }, position: { x: 0, y: 0, z: 0 }, rotation: { x: -90, y: 0, z: 0 } },
      dome: { dimensions: { width: 10, height: 5, depth: 10 }, position: { x: 0, y: 0, z: 0 } },
      pipe: { dimensions: { width: 0.3, height: 3, depth: 0.3 }, position: { x: 0, y: 1.5, z: 0 } },
      led_screen: { dimensions: { width: 0.5, height: 0.5, depth: 0.1 }, position: { x: 0, y: 2, z: 3 }, scale: { x: 0.5, y: 0.5, z: 0.1 } },
      projection_screen: { dimensions: { width: 5, height: 3, depth: 0.05 }, position: { x: 0, y: 2, z: 5 } },
      truss: { dimensions: { width: 0.5, height: 0.5, depth: 3 }, position: { x: 0, y: 3, z: 0 }, trussLength: 3 },
      scrim_screen: { dimensions: { width: 4, height: 3, depth: 0.01 }, position: { x: 0, y: 1.5, z: 2 }, scrimOpacity: 0.15 },
    };
    
    const typeMapping = {
      box: 'cube',
      sphere: 'sphere',
      plane: 'plane',
      cylinder: 'cylinder',
      ground: 'plane',
      dome: 'dome',
      pipe: 'pipe',
      led_screen: 'led_screen',
      projection_screen: 'projection_screen',
      truss: 'truss',
      scrim_screen: 'scrim_screen'
    };
    
    const config = defaults[type] || defaults.box;
    
    const baseObj = {
      name: name || type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' '),
      type: typeMapping[type] || 'cube',
      position: options.position || config.position || { x: 0, y: 1, z: 0 },
      rotation: options.rotation || config.rotation || { x: 0, y: 0, z: 0 },
      scale: options.scale || { x: 1, y: 1, z: 1 }, // Scale is now a multiplier, default 1x
      dimensions: options.dimensions || config.dimensions || { width: 1, height: 1, depth: 1 },
      color: options.color || (type === 'scrim_screen' ? '#ffffff' : '#888888'),
      trussLength: options.trussLength || config.trussLength,
      scrimOpacity: options.scrimOpacity || config.scrimOpacity
    };
    
    // Add ledConfig for all LED screen types
    if (type === 'led_screen' || type === 'led_curved' || type === 'led_corner' || type === 'led_flexible') {
      const scaleX = (options.scale?.x || config.scale?.x || 0.5);
      const scaleY = (options.scale?.y || config.scale?.y || 0.5);
      const tileW = 500;
      const tileH = 500;
      const cols = Math.max(1, Math.round((scaleX * 1000) / tileW));
      const rows = Math.max(1, Math.round((scaleY * 1000) / tileH));
      
      // Determine LED mode based on type
      let ledMode = 'standard';
      if (type === 'led_curved') ledMode = 'curved';
      else if (type === 'led_corner') ledMode = 'corner';
      else if (type === 'led_flexible') ledMode = 'flexible';
      
      baseObj.type = 'led_screen';
      baseObj.ledConfig = {
        ledMode: ledMode,
        tileWidth: tileW,
        tileHeight: type === 'led_flexible' ? tileH : tileW,
        tileDepth: type === 'led_flexible' ? 40 : 80,
        tileResH: 128,
        tileResV: 128,
        pixelPitch: type === 'led_flexible' ? 2.6 : 3.9,
        columns: type === 'led_flexible' ? 8 : cols,
        rows: rows,
        powerPerTile: type === 'led_flexible' ? 150 : 200,
        maxPowerPerTile: type === 'led_flexible' ? 200 : 250,
        curveAngle: (type === 'led_curved' || type === 'led_flexible') ? 5 : 0,
        cabinetStep: 500,
        cornerAngle: 90,
        smoothBend: type === 'led_flexible',
        contentItemId: null,
        contentUrl: null
      };
      baseObj.scale = { x: 1, y: 1, z: 1 };
    }
    
    addSceneObject(baseObj);
  };

  // Keyboard shortcuts (extracted to hook)
  useKeyboardShortcuts({
    undo, redo, duplicateSelected, duplicateMultiSelected,
    deleteSelected, selectedObjectId, toggleGrid, deselectAll,
    editMode, multiSelectIds, projectors, removeProjector,
    removeSceneObject, setMultiSelectIds, handleSave,
    setViewPreset, showAllBeams, hideAllBeams, setMeasurementMode,
    measurementMode, updateProjector, createCamera, getCameraStateRef,
  });

  return (
    <div className="menu-bar" data-testid="menu-bar">
      <Menubar className="border-0 bg-transparent h-8">
        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            File
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={onNewProject} className="text-xs">
              New Project
              <MenubarShortcut>⌘N</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={onOpenProjects} className="text-xs">
              Open Project
              <MenubarShortcut>⌘O</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={handleQuickLoad} className="text-xs">
              Load Saved
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={handleSave} className="text-xs">
              Save
              <MenubarShortcut>Ctrl+S</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={handleSaveAsClick} className="text-xs" data-testid="file-save-as">
              Save As...
              <MenubarShortcut>Ctrl+Shift+S</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={handleQuickSave} className="text-xs">
              Quick Save (Local)
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={handleExportProject} className="text-xs" data-testid="file-export-project">
              <Download className="w-3 h-3 mr-2" />
              Export Project (.avproj)
            </MenubarItem>
            <MenubarItem onClick={() => importInputRef.current?.click()} className="text-xs" data-testid="file-import-project">
              <Upload className="w-3 h-3 mr-2" />
              Import Project (.avproj)
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={handleRenameProject} className="text-xs">
              Rename Project
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={handleGetShareableLink} className="text-xs">
              <Link className="w-3 h-3 mr-2" />
              Get Shareable Link (View Only)
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Edit
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={undo} disabled={!canUndo || editMode} className="text-xs">
              Undo
              <MenubarShortcut>⌘Z</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={redo} disabled={!canRedo || editMode} className="text-xs">
              Redo
              <MenubarShortcut>⇧⌘Z</MenubarShortcut>
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={deleteSelected} disabled={!selectedObjectId} className="text-xs">
              Delete Selected
              <MenubarShortcut>⌫</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={duplicateSelected} disabled={!selectedObjectId} className="text-xs">
              Duplicate
              <MenubarShortcut>⌘D</MenubarShortcut>
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Select
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={selectAll} className="text-xs">
              Select All
              <MenubarShortcut>⌘A</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={deselectAll} className="text-xs">
              Deselect All
              <MenubarShortcut>⎋</MenubarShortcut>
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Objects
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={() => addPrimitive('box', 'Cube')} className="text-xs">
              Add Cube
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('sphere', 'Sphere')} className="text-xs">
              Add Sphere
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('plane', 'Wall/Plane')} className="text-xs">
              Add Wall/Plane
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('cylinder', 'Cylinder')} className="text-xs">
              Add Cylinder
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('ground', 'Ground')} className="text-xs">
              Add Ground
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={() => addPrimitive('dome', 'Dome')} className="text-xs">
              Add Dome
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('scrim_screen', 'Scrim Screen')} className="text-xs">
              Add Scrim Screen
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Truss
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={() => addPrimitive('truss', 'Truss 2m', { trussLength: 2 })} className="text-xs">
              2m Section
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('truss', 'Truss 3m', { trussLength: 3 })} className="text-xs">
              3m Section
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('truss', 'Truss 4m', { trussLength: 4 })} className="text-xs">
              4m Section
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('truss', 'Truss Corner', { trussLength: 0.5 })} className="text-xs">
              Corner Piece
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            LED
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={() => addPrimitive('led_screen', 'Standard LED Wall')} className="text-xs">
              Standard LED Wall
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('led_curved', 'Curved LED Wall')} className="text-xs">
              Curved LED Wall
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('led_flexible', 'Flexible LED Wall')} className="text-xs">
              Flexible LED Wall
            </MenubarItem>
            <MenubarItem onClick={() => addPrimitive('led_corner', 'Corner LED Tile')} className="text-xs">
              Corner LED Tile
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Projectors
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={() => {
              const allOn = projectors.every(p => p.power_on !== false);
              projectors.forEach(p => updateProjector(p.id, { power_on: !allOn }));
              toast.success(allOn ? 'All projectors OFF' : 'All projectors ON');
            }} className="text-xs" disabled={projectors.length === 0}>
              {projectors.every(p => p.power_on !== false) ? 'Turn All OFF' : 'Turn All ON'}
              <MenubarShortcut>Shift+P</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={showAllBeams} className="text-xs">
              Show All Beams
              <MenubarShortcut>B</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={hideAllBeams} className="text-xs">
              Hide All Beams
              <MenubarShortcut>Shift+B</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={() => {
              const allBorders = projectors.every(p => p.show_border !== false);
              projectors.forEach(p => updateProjector(p.id, { show_border: !allBorders }));
              toast.success(allBorders ? 'All borders OFF' : 'All borders ON');
            }} className="text-xs" disabled={projectors.length === 0}>
              {projectors.every(p => p.show_border !== false) ? 'Hide All Borders' : 'Show All Borders'}
              <MenubarShortcut>Shift+O</MenubarShortcut>
            </MenubarItem>
            <MenubarSeparator />
            {/* One Add submenu per brand returned by the backend (Barco,
                Panasonic, Christie, Epson, Sharp/NEC, Optoma, BenQ, ...). */}
            {Object.entries(projectorLibrary?.brands || {})
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([brandLabel, projs]) => (
                <MenubarSub key={brandLabel}>
                  <MenubarSubTrigger className="text-xs">
                    {`Add ${brandLabel}`}
                  </MenubarSubTrigger>
                  <MenubarSubContent className="bg-zinc-800 border-zinc-700">
                    {(projs || []).slice(0, 12).map(proj => (
                      <MenubarItem
                        key={proj.id}
                        className="text-xs"
                        onClick={() => openAddProjectorDialog({ ...proj })}
                      >
                        {proj.model}
                      </MenubarItem>
                    ))}
                  </MenubarSubContent>
                </MenubarSub>
              ))}
            <MenubarSeparator />
            <MenubarItem className="text-xs" onClick={() => {
              setLeftPanelOpen(true);
              setActiveLeftTab('projectors');
            }}>
              Open Projector Library
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem 
              onClick={handleExportProjectionReport} 
              disabled={projReportExporting || projectors.length === 0}
              className="text-xs"
              data-testid="export-projection-report"
            >
              <FileText className="w-3 h-3 mr-2" />
              {projReportExporting ? 'Generating Report...' : 'Export Projection Report'}
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Viewport
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={() => setViewPreset('reset')} className="text-xs">
              Perspective (Reset)
              <MenubarShortcut>0</MenubarShortcut>
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={() => setViewPreset('top')} className="text-xs">
              Top View
              <MenubarShortcut>Num 7</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={() => setViewPreset('bottom')} className="text-xs">
              Bottom View
              <MenubarShortcut>Ctrl+7</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={() => setViewPreset('front')} className="text-xs">
              Front View
              <MenubarShortcut>Num 1</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={() => setViewPreset('back')} className="text-xs">
              Back View
              <MenubarShortcut>Ctrl+1</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={() => setViewPreset('left')} className="text-xs">
              Left View
              <MenubarShortcut>Num 3</MenubarShortcut>
            </MenubarItem>
            <MenubarItem onClick={() => setViewPreset('right')} className="text-xs">
              Right View
              <MenubarShortcut>Ctrl+3</MenubarShortcut>
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={toggleGrid} className="text-xs">
              Toggle Grid
              <MenubarShortcut>G</MenubarShortcut>
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Camera
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem onClick={() => {
              if (getCameraStateRef?.current) {
                const state = getCameraStateRef.current();
                if (state) { createCamera(state); toast.success('Camera added'); }
              }
            }} className="text-xs">
              Add Camera from Current View
              <MenubarShortcut>Ctrl+Shift+C</MenubarShortcut>
            </MenubarItem>
            <MenubarSeparator />
            {savedCameras.length > 0 ? savedCameras.map((cam, i) => (
              <MenubarItem key={cam.id} onClick={() => {
                if (window.focusCameraView) window.focusCameraView(cam.id);
              }} className="text-xs">
                {cam.name}
              </MenubarItem>
            )) : (
              <MenubarItem disabled className="text-xs text-zinc-500">No saved cameras</MenubarItem>
            )}
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            <Ruler className="w-3 h-3 mr-1 inline" />
            Measure
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem 
              onClick={() => {
                setMeasurementMode(!measurementMode);
                if (!measurementMode) {
                  toast.info('Click first point to start measuring');
                }
              }} 
              className="text-xs"
            >
              {measurementMode ? '✓ ' : ''}Point-to-Point Distance
              <MenubarShortcut>M</MenubarShortcut>
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={clearAllMeasurements} className="text-xs text-red-400">
              Clear All Measurements
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger className="text-xs px-3 text-zinc-400 hover:text-white data-[state=open]:text-white">
            Help
          </MenubarTrigger>
          <MenubarContent className="bg-zinc-800 border-zinc-700">
            <MenubarItem className="text-xs" onClick={onOpenManual} data-testid="help-user-manual">
              <BookOpen className="w-3 h-3 mr-2" />
              User Manual
            </MenubarItem>
            <MenubarItem
              className="text-xs"
              onClick={() => setPerfGuideOpen(true)}
              data-testid="help-performance-guide"
            >
              <Zap className="w-3 h-3 mr-2" />
              Performance &amp; Setup Guide
            </MenubarItem>
            <MenubarItem
              className="text-xs"
              onClick={() => setSupportOpen(true)}
              data-testid="help-contact-support"
            >
              <MessageCircle className="w-3 h-3 mr-2" />
              Contact Support
              {supportUnread > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-fuchsia-600 text-white text-[9px] font-bold">
                  {supportUnread > 9 ? '9+' : supportUnread}
                </span>
              )}
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem
              className="text-xs"
              onClick={() => setTermsOpen(true)}
              data-testid="help-terms-conditions"
            >
              <FileText className="w-3 h-3 mr-2" />
              Terms &amp; Conditions
            </MenubarItem>
            <MenubarItem className="text-xs" onClick={() => {
              toast.info(
                <div className="text-xs space-y-1">
                  <div><strong>General:</strong></div>
                  <div>Ctrl+S - Save | Ctrl+Z - Undo | Ctrl+Shift+Z - Redo</div>
                  <div>Ctrl+D - Duplicate | Del - Delete | Esc - Deselect</div>
                  <div><strong>Transform:</strong></div>
                  <div>W - Move | E - Rotate | R - Scale | V - Select</div>
                  <div><strong>Viewport:</strong></div>
                  <div>0 - Perspective | 1 - Front | Ctrl+1 - Back</div>
                  <div>3 - Right | Ctrl+3 - Left | 7 - Top | Ctrl+7 - Bottom</div>
                  <div><strong>Tools:</strong></div>
                  <div>G - Toggle Grid | M - Measure | B - Show Beams</div>
                  <div>Shift+B - Hide Beams | Shift+P - Projectors On/Off</div>
                  <div>Shift+O - Borders On/Off | Ctrl+Shift+C - Add Camera</div>
                </div>
              );
            }}>
              Keyboard Shortcuts
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem className="text-xs" onClick={() => toast.info('IAV - Projection Mapping Simulator v1.0')}>
              About IAV
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>

      {/* Project name — click to rename in-place. Enter / blur commits,
          Esc cancels. Hover shows a pencil affordance on the right. */}
      <div className="flex items-center ml-4 text-xs text-zinc-500 group">
        {renamingProject ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onBlur={commitRenameProject}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRenameProject(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelRenameProject(); }
            }}
            spellCheck={false}
            maxLength={120}
            className="bg-zinc-800 border border-zinc-600 rounded px-1.5 py-0.5 text-xs text-zinc-100 focus:outline-none focus:border-fuchsia-500 min-w-[160px]"
            data-testid="project-name-input"
          />
        ) : (
          <button
            type="button"
            onClick={startRenameProject}
            onDoubleClick={startRenameProject}
            title="Click to rename project"
            className="flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-zinc-800 transition-colors cursor-text"
            data-testid="project-name-rename-trigger"
          >
            <span className="text-zinc-300">
              {currentProject ? currentProject.name : (projectSettings?.name || 'Untitled')}
            </span>
            {isDirty && <span className="text-fuchsia-400">*</span>}
            <Pencil className="w-3 h-3 text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* User Menu */}
      <div className="flex items-center gap-2 mr-2">
        <Menubar className="border-0 bg-transparent h-8">
          <MenubarMenu>
            <MenubarTrigger className="text-xs px-2 text-zinc-400 hover:text-white data-[state=open]:text-white flex items-center gap-1.5" data-testid="user-menu-trigger">
              <User className="w-3.5 h-3.5" />
              <span className="max-w-[100px] truncate">{user?.name || user?.email}</span>
            </MenubarTrigger>
            <MenubarContent className="bg-zinc-800 border-zinc-700" align="end">
              <div className="px-3 py-2 border-b border-zinc-700">
                <p className="text-xs font-medium text-white">{user?.name}</p>
                <p className="text-[10px] text-zinc-400">{user?.email}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">{user?.serial}</p>
              </div>
              <MenubarItem className="text-xs" onClick={() => window.location.href = '/settings'} data-testid="menu-settings-btn">
                <Settings className="w-3.5 h-3.5 mr-2" /> Settings
              </MenubarItem>
              {isAdmin && (
                <MenubarItem className="text-xs" onClick={() => window.location.href = '/admin'} data-testid="menu-admin-btn">
                  <Shield className="w-3.5 h-3.5 mr-2 text-violet-400" /> Admin Panel
                </MenubarItem>
              )}
              <MenubarSeparator />
              <MenubarItem className="text-xs text-red-400" onClick={logout} data-testid="menu-logout-btn">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>

      {/* Shareable Link Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link className="w-5 h-5 text-fuchsia-400" />
              Shareable Link (View Only)
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Share this link with clients for a view-only 3D preview. They can orbit, pan, and switch between camera views.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                value={shareLink}
                readOnly
                className="bg-zinc-800 border-zinc-700 text-zinc-200 text-sm font-mono"
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="sm"
                className="shrink-0"
              >
                {linkCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-3 space-y-2">
              <p className="text-xs text-zinc-400 font-medium">Viewer Features:</p>
              <ul className="text-xs text-zinc-500 space-y-1">
                <li>• 3D View with orbit and pan controls</li>
                <li>• Camera presets: Top, Bottom, Left, Right, Front, Back</li>
                <li>• Grid toggle</li>
                <li>• View only - no editing capabilities</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShareDialogOpen(false)}
              className="text-xs"
            >
              Close
            </Button>
            <Button
              onClick={() => window.open(shareLink, '_blank')}
              className="text-xs bg-fuchsia-600 hover:bg-fuchsia-700"
            >
              Open Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save As Dialog */}
      <Dialog open={saveAsDialogOpen} onOpenChange={setSaveAsDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Save As New Project</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={saveAsNameVal}
              onChange={(e) => setSaveAsNameVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsConfirm(); }}
              placeholder="Enter project name..."
              className="bg-zinc-800 border-zinc-600 text-zinc-100"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveAsDialogOpen(false)} className="text-xs text-zinc-400">Cancel</Button>
            <Button onClick={handleSaveAsConfirm} className="text-xs bg-fuchsia-600 hover:bg-fuchsia-700">Save As</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden file input for .avproj import */}
      <input
        ref={importInputRef}
        type="file"
        accept=".avproj,application/zip"
        onChange={handleImportFileSelected}
        style={{ display: 'none' }}
        data-testid="import-project-file-input"
      />

      {/* Import preview/commit modal */}
      <ImportProjectModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        pkg={importPkg}
        onConfirm={handleImportConfirm}
      />

      {/* Performance & Setup Guide */}
      <PerformanceGuideModal
        open={perfGuideOpen}
        onOpenChange={setPerfGuideOpen}
      />

      {/* Contact Support */}
      <ContactSupportModal
        open={supportOpen}
        onOpenChange={setSupportOpen}
      />

      {/* Terms & Conditions */}
      <TermsModal
        open={termsOpen}
        onOpenChange={setTermsOpen}
      />
    </div>
  );
};

export default MenuBar;
